import React from "react";

// Lightweight, dependency-free, XSS-safe markdown renderer.
// Supports: ## / ### headings, paragraphs, - / * bullet lists, > blockquotes, **bold** inline.
// Renders React elements only (no dangerouslySetInnerHTML).

function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={`${keyPrefix}-${i}`} className="text-[var(--text)] font-medium">{p.slice(2, -2)}</strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{p}</React.Fragment>;
  });
}

export default function Markdown({ text, className = "" }) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (line.startsWith("### ")) { blocks.push({ type: "h3", text: line.slice(4) }); i++; continue; }
    if (line.startsWith("## ")) { blocks.push({ type: "h2", text: line.slice(3) }); i++; continue; }
    if (line.startsWith("# ")) { blocks.push({ type: "h2", text: line.slice(2) }); i++; continue; }
    if (line.startsWith("> ")) {
      const q = [];
      while (i < lines.length && lines[i].startsWith("> ")) { q.push(lines[i].slice(2)); i++; }
      blocks.push({ type: "quote", text: q.join(" ") });
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("* "))) { items.push(lines[i].slice(2)); i++; }
      blocks.push({ type: "ul", items });
      continue;
    }
    const para = [];
    while (
      i < lines.length && lines[i].trim() &&
      !lines[i].startsWith("#") && !lines[i].startsWith("> ") &&
      !lines[i].startsWith("- ") && !lines[i].startsWith("* ")
    ) { para.push(lines[i]); i++; }
    blocks.push({ type: "p", text: para.join(" ") });
  }

  return (
    <div className={className} data-testid="markdown-body">
      {blocks.map((b, idx) => {
        if (b.type === "h2") return <h2 key={idx} className="text-2xl font-semibold text-[var(--text)] mt-10 mb-3">{renderInline(b.text, `h2${idx}`)}</h2>;
        if (b.type === "h3") return <h3 key={idx} className="text-lg font-semibold text-[var(--text)] mt-8 mb-2">{renderInline(b.text, `h3${idx}`)}</h3>;
        if (b.type === "quote") return <blockquote key={idx} className="border-l-2 border-[#c9a86a] pl-5 my-7 text-xl italic text-[var(--text)]">{renderInline(b.text, `q${idx}`)}</blockquote>;
        if (b.type === "ul") return (
          <ul key={idx} className="my-4 space-y-2">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2 text-[var(--text-2)] leading-relaxed"><span className="text-[#c9a86a] mt-0.5">—</span><span>{renderInline(it, `li${idx}-${j}`)}</span></li>
            ))}
          </ul>
        );
        return <p key={idx} className="text-[var(--text-2)] leading-relaxed my-4">{renderInline(b.text, `p${idx}`)}</p>;
      })}
    </div>
  );
}
